import type { LogicalCondition, VectorSearchParams } from "@rebasepro/types";
import { toCanonicalOp } from "@rebasepro/types";
import { deserializeOrderBy, deserializeFilter, deserializeLogicalCondition } from "@rebasepro/common";
import { QueryOptions } from "../types";

export const mapOperator = (op: string) => toCanonicalOp(op) ?? null;

function getLastValue(val: unknown): unknown {
    if (Array.isArray(val)) {
        return val[val.length - 1];
    }
    return val;
}

/**
 * Parse an `or(...)` / `and(...)` logical group from its wire form.
 *
 * The wire carries the inner conditions wrapped in parens (e.g.
 * `(status.eq.active,age.gte.18)`); we re-attach the `or`/`and` prefix and
 * delegate to the canonical filter dialect (`@rebasepro/common`). Values are
 * preserved as strings — type coercion is the schema-aware driver's job, so
 * this path stays byte-for-byte consistent with the SDK/admin path (which
 * also parses via the shared dialect).
 */
function parseLogicalGroup(type: "or" | "and", raw: unknown): LogicalCondition | undefined {
    let inner = String(raw).trim();
    if (inner.startsWith("(") && inner.endsWith(")")) {
        inner = inner.slice(1, -1);
    }
    inner = inner.trim();
    if (!inner) return undefined;
    const parsed = deserializeLogicalCondition(`${type}(${inner})`);
    return "type" in parsed ? parsed : undefined;
}

/**
 * Parse query parameters into QueryOptions
 */
export function parseQueryOptions(query: Record<string, unknown>): QueryOptions {
    const options: QueryOptions = {};

    // Pagination
    const limitVal = getLastValue(query.limit);
    if (limitVal) options.limit = parseInt(String(limitVal));

    const offsetVal = getLastValue(query.offset);
    if (offsetVal) options.offset = parseInt(String(offsetVal));

    const pageVal = getLastValue(query.page);
    if (pageVal) {
        const page = parseInt(String(pageVal));
        const limit = options.limit || 20;
        options.offset = (page - 1) * limit;
    }

    // ── Logical conditions (or / and) ──────────────────────────────────
    const orVal = getLastValue(query.or);
    const andVal = getLastValue(query.and);
    if (orVal) {
        const logical = parseLogicalGroup("or", orVal);
        if (logical) options.logical = logical;
    } else if (andVal) {
        const logical = parseLogicalGroup("and", andVal);
        if (logical) options.logical = logical;
    }

    // ── PostgREST-style field filters: ?field=op.value ─────────────────
    // Delegate to the canonical filter dialect (the single source of truth
    // for the wire grammar: operator codes, list/escape handling, implicit
    // eq). Values stay strings; the schema-aware driver coerces them to
    // column types. This keeps the REST path byte-for-byte consistent with
    // the SDK/admin path, which parses through the same `deserializeFilter`.
    const reservedQueryKeys = ["limit", "offset", "page", "orderBy", "include", "fields", "searchString", "vector_search", "vector", "vector_distance", "vector_threshold", "or", "and"];
    const filterDict: Record<string, unknown> = {};
    for (const [key, rawValue] of Object.entries(query)) {
        if (reservedQueryKeys.includes(key)) continue;
        filterDict[key] = rawValue;
    }
    const where = deserializeFilter(filterDict);
    if (Object.keys(where).length > 0) {
        options.where = where;
    }

    // Sorting
    const orderByVal = getLastValue(query.orderBy);
    if (orderByVal) {
        try {
            options.orderBy = typeof orderByVal === "string"
                ? JSON.parse(orderByVal)
                : orderByVal;
        } catch {
            // Try simple format: "field:direction"
            if (typeof orderByVal === "string") {
                const parsed = deserializeOrderBy(orderByVal);
                if (parsed) {
                    options.orderBy = [
                        {
                            field: parsed[0],
                            direction: parsed[1]
                        }
                    ];
                }
            }
        }
    }

    // Relation includes
    const includeVal = getLastValue(query.include);
    if (includeVal) {
        const includeStr = String(includeVal).trim();
        if (includeStr === "*") {
            options.include = ["*"];
        } else {
            options.include = includeStr.split(",").map(s => s.trim()).filter(Boolean);
        }
    }

    // Field selection
    const fieldsVal = getLastValue(query.fields);
    if (fieldsVal) {
        const fieldsStr = String(fieldsVal).trim();
        options.fields = fieldsStr.split(",").map(s => s.trim()).filter(Boolean);
    }

    // Vector similarity search
    const vectorSearchVal = getLastValue(query.vector_search);
    const vectorVal = getLastValue(query.vector);
    if (vectorSearchVal && vectorVal) {
        const vectorStr = String(vectorVal);
        let queryVector: number[];
        try {
            queryVector = JSON.parse(vectorStr) as number[];
            if (!Array.isArray(queryVector) || !queryVector.every(v => typeof v === "number")) {
                throw new Error("Expected array of numbers");
            }
        } catch {
            throw new Error("Invalid vector format. Expected JSON array of numbers, e.g. [0.1,0.2,0.3]");
        }

        const distanceParamVal = getLastValue(query.vector_distance);
        const distanceParam = distanceParamVal ? String(distanceParamVal) : "cosine";
        if (distanceParam !== "cosine" && distanceParam !== "l2" && distanceParam !== "inner_product") {
            throw new Error(`Invalid vector_distance: ${distanceParam}. Expected: cosine, l2, or inner_product`);
        }

        const vectorSearch: VectorSearchParams = {
            property: String(vectorSearchVal),
            vector: queryVector,
            distance: distanceParam
        };

        const thresholdVal = getLastValue(query.vector_threshold);
        if (thresholdVal) {
            const threshold = parseFloat(String(thresholdVal));
            if (isNaN(threshold)) {
                throw new Error("Invalid vector_threshold. Expected a number.");
            }
            vectorSearch.threshold = threshold;
        }

        options.vectorSearch = vectorSearch;
    }

    return options;
}
